import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Container,
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  MicNone as MicIcon,
  Article as ArticleIcon,
  PlayCircleOutline as PlayIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { lionVoiceAPI } from '../services/api';
import { colors } from '../theme';

const LionVoiceEpisodePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    ['lionvoice-episode', id],
    () => lionVoiceAPI.getById(id),
    { enabled: !!id }
  );

  const episode = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !episode) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">載入節目資料時發生錯誤，請稍後再試。</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          返回獅聲心語
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="lg" sx={{ pt: 3, pb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 2, color: colors.textPrimary }}
        >
          返回獅聲心語
        </Button>
      </Container>

      {/* Banner */}
      <Container maxWidth="lg">
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {episode.banner_image ? (
            <Box
              component="img"
              src={episode.banner_image}
              alt={episode.title}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
          ) : (
            <Box sx={{ minHeight: { xs: 200, md: 320 }, width: '100%' }} />
          )}
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Chip
            label={`第${episode.episode_number}集`}
            size="small"
            sx={{ backgroundColor: colors.lionsGold, color: colors.royalBlue, fontWeight: 700 }}
          />
          {episode.published_date && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              {new Date(episode.published_date).toLocaleDateString('zh-HK', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          )}
        </Box>
        <Typography
          variant="h4"
          component="h1"
          sx={{ color: colors.royalBlue, fontWeight: 700, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}
        >
          {episode.title}
        </Typography>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {episode.description && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.9, color: colors.textPrimary }}>
                  {episode.description}
                </Typography>
              </Paper>
            )}

            {/* Audio Player */}
            {episode.audio_file && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PlayIcon sx={{ color: colors.royalBlue }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.royalBlue }}>
                    收聽節目
                  </Typography>
                </Box>
                <audio
                  controls
                  controlsList="nodownload"
                  style={{ width: '100%', borderRadius: 8 }}
                  src={episode.audio_file}
                >
                  您的瀏覽器不支援音頻播放。
                </audio>
              </Paper>
            )}

            {/* Intro Content */}
            {episode.intro_content && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MicIcon sx={{ color: colors.royalBlue }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.royalBlue }}>
                    內容介紹
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  className="richtext-content"
                  dangerouslySetInnerHTML={{ __html: episode.intro_content }}
                  sx={{
                    lineHeight: 1.9,
                    '& img': { maxWidth: '100% !important', width: 'auto !important', height: 'auto !important', borderRadius: 1, my: 1, display: 'block' },
                    '& p': { mb: 1.5 },
                    '& ul, & ol': { pl: 3, mb: 1.5 },
                    '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, color: colors.royalBlue },
                    '& a': { color: colors.royalBlue },
                  }}
                />
              </Paper>
            )}

            {/* Featured Article */}
            {(episode.featured_article_title || episode.featured_article_content) && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2, borderLeft: `4px solid ${colors.lionsGold}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ArticleIcon sx={{ color: colors.lionsGold }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.royalBlue }}>
                    精選文章
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {episode.featured_article_title && (
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 700, mb: 2, color: colors.royalBlue, fontSize: '1.2rem' }}
                  >
                    {episode.featured_article_title}
                  </Typography>
                )}
                {episode.featured_article_content && (
                  <Box
                    className="richtext-content"
                    dangerouslySetInnerHTML={{ __html: episode.featured_article_content }}
                    sx={{
                      lineHeight: 1.9,
                      '& img': { maxWidth: '100% !important', width: 'auto !important', height: 'auto !important', borderRadius: 1, my: 1, display: 'block' },
                      '& p': { mb: 1.5 },
                      '& ul, & ol': { pl: 3, mb: 1.5 },
                      '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, color: colors.royalBlue },
                      '& a': { color: colors.royalBlue },
                    }}
                  />
                )}

                {(episode.video_file || episode.video_url) && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid rgba(0,0,0,0.12)` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PlayIcon sx={{ color: colors.lionsGold }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.royalBlue }}>
                        精選影片
                      </Typography>
                    </Box>
                    {episode.video_file && (
                      <video
                        controls
                        style={{ width: '100%', borderRadius: 8, marginTop: '8px' }}
                        src={episode.video_file}
                      >
                        您的瀏覽器不支援影片播放。
                      </video>
                    )}
                    {!episode.video_file && episode.video_url && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          href={episode.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<OpenInNewIcon />}
                          variant="contained"
                          sx={{ backgroundColor: colors.royalBlue }}
                        >
                          觀看影片
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            )}
          </Grid>

          {/* Sidebar: Guests */}
          <Grid item xs={12} md={4}>
            {episode.guests && episode.guests.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 2, position: { md: 'sticky' }, top: { md: 80 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.royalBlue, mb: 2 }}>
                  本集嘉賓
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {episode.guests.map((guest) => (
                    <Box key={guest.id}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {guest.name}
                      </Typography>
                      {guest.role && (
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                          {guest.role}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LionVoiceEpisodePage;
